import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { Search, User, Car, UserPlus, Loader2, Lock, Upload, ImageOff, ShieldAlert, ExternalLink, Link2, X } from "lucide-react";
import { FederalDb } from "@/components/FederalDb";
import { StateRegistries } from "@/components/StateRegistries";
import { PersonProfileCard } from "@/components/PersonProfileCard";

const DISPO_COLOR = { pending: "#F59E0B", guilty: "#EF4444", convicted_lesser: "#EF4444", not_guilty: "#10B981", acquitted: "#10B981", dismissed: "#3B82F6", nolle_prosequi: "#3B82F6", withdrawn: "#3B82F6", pbj: "#A78BFA", stet: "#22D3EE", transferred: "#9CA3AF", other: "#9CA3AF" };
const DISPO_LABEL = { pending: "PENDING", guilty: "GUILTY", not_guilty: "NOT GUILTY", dismissed: "DISMISSED", nolle_prosequi: "NOLLE PROS", pbj: "PBJ", stet: "STET", acquitted: "ACQUITTED", convicted_lesser: "CONVICTED (LESSER)", transferred: "TRANSFERRED", withdrawn: "WITHDRAWN", other: "OTHER" };

function LocalChargesBadges({ rec, onOpen }) {
  const lc = rec.local_charges;
  if (!lc || !lc.charges) return null;
  return (
    <div data-testid={`ncic-record-${rec.id}-charges`} className="flex items-center gap-1.5 flex-wrap mt-1.5">
      <span className="font-mono text-[9px] text-[#6B7280] uppercase">Local RMS · {lc.charges} charge{lc.charges === 1 ? "" : "s"}:</span>
      {Object.entries(lc.by_disposition).map(([k, n]) => (
        <span key={k} data-testid={`ncic-record-${rec.id}-dispo-${k}`} className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded-sm"
          style={{ background: `${DISPO_COLOR[k] || "#9CA3AF"}22`, color: DISPO_COLOR[k] || "#9CA3AF", border: `1px solid ${DISPO_COLOR[k] || "#9CA3AF"}66` }}>
          {n}× {DISPO_LABEL[k] || k}
        </span>
      ))}
      <button data-testid={`ncic-record-${rec.id}-open-profile`} onClick={onOpen} className="ml-1 px-1.5 py-0.5 text-[9px] font-bold border border-[#2C2D35] text-[#9CA3AF] hover:text-white hover:border-white transition-colors">OPEN FULL PROFILE</button>
    </div>
  );
}

const PERSON_FIELDS = [["name", "Name"], ["dob", "Date of Birth"]];
const VEHICLE_FIELDS = [["plate", "License Plate"], ["vin", "VIN"]];
const STATUS_OPTIONS = ["CLEAR", "WANTED", "PROTECTION ORDER", "MISSING", "FELONY", "PERSON OF INTEREST"];
const STATUS_COLOR = {
  WANTED: "#DC2626", "FELONY VEHICLE": "#DC2626", FELONY: "#DC2626", STOLEN: "#DC2626",
  "PROTECTION ORDER": "#F59E0B", MISSING: "#F59E0B", "PERSON OF INTEREST": "#F59E0B", CLEAR: "#10B981",
};

// Authenticated image (img src cannot carry a Bearer header → fetch as blob).
function AuthImg({ path, className, size = 56 }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url;
    setSrc(null); setFailed(false);
    if (!path) return;
    client.get(`/ncic/photo/${path}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setFailed(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!path || failed)
    return <div className={className} style={{ width: size, height: size }}
      data-testid="ncic-photo-empty">
      <div className="w-full h-full flex items-center justify-center bg-[#0d0e11] border border-[#2C2D35] text-[#4B5563]"><ImageOff size={18} /></div>
    </div>;
  return src
    ? <img src={src} alt="subject" data-testid="ncic-photo" className={`${className} object-cover border border-[#2C2D35]`} style={{ width: size, height: size }} />
    : <div className={className} style={{ width: size, height: size }}><div className="w-full h-full flex items-center justify-center bg-[#0d0e11] border border-[#2C2D35]"><Loader2 size={16} className="animate-spin text-[#9CA3AF]" /></div></div>;
}

// Sex Offender Registry gateway — disclaimer gate, 5 audited searches/day, then
// hands the operator the official public registry portals (NSOPW has no public API).
function OffenderRegistry() {
  const [agreed, setAgreed] = useState(sessionStorage.getItem("sor_disclaimer_agreed") === "1");
  const [chk, setChk] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const limit = 5;
  useEffect(() => {
    client.get("/ncic/offender-registry/status").then((r) => setRemaining(r.data.remaining)).catch(() => {});
  }, []);
  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await client.post("/ncic/offender-registry/search", { query: q, disclaimer_agreed: true });
      setRes(data); setRemaining(data.remaining);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Registry search failed");
    } finally { setBusy(false); }
  };

  if (!agreed) {
    return (
      <div data-testid="sor-disclaimer" className="border border-[#2C2D35] bg-[#101114]">
        <div className="px-4 pt-4 flex items-start gap-2.5">
          <ShieldAlert size={20} className="text-[#F59E0B] mt-0.5 shrink-0" />
          <div>
            <div className="font-head font-bold text-base text-[#F59E0B]">Sex Offender Registry — Disclaimer</div>
            <div className="font-mono text-xs text-[#9CA3AF] mt-1">Welcome to the Sex Offender Registry Search. Please read and agree to the following before proceeding:</div>
          </div>
        </div>
        <div className="m-4 border border-[#2C2D35] bg-[#141518] p-4 space-y-3 text-sm text-[#C9CDD4] leading-relaxed">
          <p><b className="text-white">Purpose:</b> This tool searches publicly available sex offender registry data from NSOPW.gov, the Maryland DPSCS registry, and other state registries. Information is provided solely for public safety awareness.</p>
          <p><b className="text-white">Accuracy:</b> Registry information is self-reported by registrants and may be time-sensitive. The data cannot be guaranteed to be accurate, complete, or current. Registrants may have moved without notifying the Department.</p>
          <p><b className="text-white">Prohibited Use:</b> This information shall <b className="text-[#EF4444]">NOT</b> be used to injure, harass, or commit any criminal act against any individual named in the registry or residing at a reported address. Such actions may subject you to criminal prosecution.</p>
          <p><b className="text-white">Identification:</b> A name alone is insufficient to positively identify a registrant. Positive identification requires fingerprint verification.</p>
          <p><b className="text-white">Reporting Violations:</b> If you believe a registrant is not complying with registration requirements, contact your local law enforcement agency or the Maryland DPSCS.</p>
          <p className="font-bold text-[#F59E0B]">By clicking "I Agree &amp; Continue", you acknowledge that you have read and understand these terms and agree to use this information only for lawful purposes.</p>
        </div>
        <label className="flex items-center gap-2.5 px-4 text-sm text-white cursor-pointer">
          <input data-testid="sor-agree-check" type="checkbox" checked={chk} onChange={(e) => setChk(e.target.checked)} className="w-4 h-4" />
          I have read and agree to the above terms and conditions
        </label>
        <div className="p-4">
          <button data-testid="sor-agree-btn" disabled={!chk}
            onClick={() => { sessionStorage.setItem("sor_disclaimer_agreed", "1"); setAgreed(true); }}
            className="w-full py-2.5 font-head font-bold text-sm text-white disabled:opacity-50"
            style={{ background: "#B45309" }}>
            I Agree &amp; Continue to Registry Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sor-search" className="border border-[#2C2D35] bg-[#101114] p-4">
      <div className="font-head font-bold text-base text-white">Sex Offender Registry Search</div>
      <div className="font-mono text-xs text-[#9CA3AF] mt-1 mb-3">
        Searches NSOPW and all state registries (PA, MD, NC, FL, TX + national). Enter a full name or address.
        Limit: <b className="text-[#F59E0B]" data-testid="sor-remaining">{remaining ?? "…"}</b> of {limit} searches remaining today.
      </div>
      <div className="flex gap-2 mb-3">
        <input data-testid="sor-query" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="e.g. John Smith or 123 Main St Baltimore MD"
          className="flex-1 bg-[#141518] border border-[#2C2D35] px-3 py-2.5 text-sm outline-none focus:border-[#F59E0B] font-mono rounded-full" />
        <button data-testid="sor-search-btn" onClick={search} disabled={busy || remaining === 0}
          className="px-5 bg-white text-black flex items-center gap-1.5 font-head text-sm font-bold rounded-md disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : "Search"}
        </button>
      </div>
      <div className="font-mono text-[11px] text-[#60A5FA] border border-[#1E3A5F] bg-[#0d1524] px-3 py-2 mb-3">
        ℹ Searches NSOPW.gov, Maryland DPSCS, and individual state registries for the most current public registry data. Every search is CJIS-audited.
      </div>
      {remaining === 0 && !res && <div className="font-mono text-xs text-[#EF4444] border border-[#7F1D1D] p-2 mb-3">Daily registry search limit reached (5 per day). Resets at midnight UTC.</div>}
      {res && (
        <div data-testid="sor-results" className="space-y-2">
          <div className="font-mono text-[11px] text-[#9CA3AF]">Official registry portals for “<span className="text-white">{res.query}</span>” — open each to run the query at the authoritative source:</div>
          {res.registries.map((r) => (
            <div key={r.url} className="border border-[#2C2D35] bg-[#141518] p-3 flex items-center gap-3" data-testid={`sor-registry-${r.region.toLowerCase()}`}>
              <span className="font-mono text-[9px] font-bold px-1.5 py-0.5 border shrink-0" style={{ color: r.region === "NATIONAL" ? "#F59E0B" : "#60A5FA", borderColor: "#2C2D35" }}>{r.region}</span>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white font-semibold truncate">{r.name}</div>
                <div className="font-mono text-[10px] text-[#6B7280]">{r.coverage}</div>
              </div>
              <a href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`sor-open-${r.region.toLowerCase()}`}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-head font-bold text-black bg-[#F59E0B] shrink-0">
                <ExternalLink size={12} /> OPEN
              </a>
            </div>
          ))}
          <div className="font-mono text-[10px] text-[#9CA3AF] border border-[#2C2D35] p-2">
            NSOPW.gov does not offer a public data API — these portals are the authoritative sources. Positive identification requires fingerprint verification; verify with the registering agency before any action.
          </div>
        </div>
      )}
    </div>
  );
}

// BPD Officer List — imported "Active Employees as of May 3, 2018" roster.
// Searchable by name, EMPLID, SEQ#, job title or location; lookups are CJIS-audited.
function BpdRoster() {
  const [q, setQ] = useState("");
  const [field, setField] = useState("any");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async (query = q, f = field) => {
    setBusy(true);
    try {
      const r = await client.get("/ncic/bpd-officers", { params: { q: query, field: f, limit: 100 } });
      setData(r.data);
    } catch { toast.error("Roster search failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load("", "any"); }, []); // eslint-disable-line
  const SEARCH_FIELDS = [["any", "All fields"], ["name", "Name"], ["emplid", "EMPLID"], ["seq", "SEQ#"], ["job_title", "Job Title"], ["location", "Location/Unit"]];
  return (
    <div data-testid="bpd-roster">
      <div className="flex gap-2 mb-3">
        <select data-testid="bpd-field" value={field} onChange={(e) => setField(e.target.value)}
          className="bg-[#141518] border border-[#2C2D35] px-2 py-2 text-sm font-mono text-white outline-none focus:border-[#3B82F6]">
          {SEARCH_FIELDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid="bpd-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search name, EMPLID, SEQ#, job title, unit…"
          className="flex-1 bg-[#141518] border border-[#2C2D35] px-3 py-2.5 text-sm outline-none focus:border-[#3B82F6] font-mono" />
        <button data-testid="bpd-search-btn" onClick={() => load()} disabled={busy}
          className="px-4 bg-[#3B82F6] text-white flex items-center gap-1.5 font-head text-sm">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
        </button>
      </div>
      <div className="bg-[#3B82F6] text-white font-mono text-[11px] font-bold px-3 py-1.5 mb-3">
        BPD OFFICER LIST · SOURCE: ACTIVE EMPLOYEES AS OF MAY 3, 2018 · {data ? `${data.shown} OF ${data.total} SHOWN` : "LOADING…"} · SEARCHES AUDITED
      </div>
      <div className="max-h-[520px] overflow-y-auto border border-[#2C2D35]">
        <table className="w-full text-left">
          <thead className="sticky top-0 bg-[#141518] z-10">
            <tr className="font-head text-[9px] text-[#6B7280] tracking-wider">
              <th className="px-2 py-1.5">NAME</th><th className="px-2 py-1.5">EMPLID</th>
              <th className="px-2 py-1.5">SEQ#</th><th className="px-2 py-1.5">JOB TITLE</th>
              <th className="px-2 py-1.5">UNIT/LOC</th><th className="px-2 py-1.5">GRADE</th>
              <th className="px-2 py-1.5">SERVICE DATE</th><th className="px-2 py-1.5">PROMOTED</th>
              <th className="px-2 py-1.5">SEX</th><th className="px-2 py-1.5">SUPV ID</th>
            </tr>
          </thead>
          <tbody data-testid="bpd-results">
            {(data?.officers || []).map((o) => (
              <tr key={o.id} className="border-t border-[#1F2026] font-mono text-[11px] text-[#C9CDD4]">
                <td className="px-2 py-1.5 text-white font-bold">{o.last_name}, {o.first_name} {o.middle_name}</td>
                <td className="px-2 py-1.5">{o.emplid}</td>
                <td className="px-2 py-1.5 text-[#F59E0B]">{o.seq || "—"}</td>
                <td className="px-2 py-1.5">{o.job_title}</td>
                <td className="px-2 py-1.5">{o.location}</td>
                <td className="px-2 py-1.5">{o.grade}</td>
                <td className="px-2 py-1.5">{o.service_date}</td>
                <td className="px-2 py-1.5">{o.promotion_date || "—"}</td>
                <td className="px-2 py-1.5">{o.sex}</td>
                <td className="px-2 py-1.5">{o.supv_id || "—"}</td>
              </tr>
            ))}
            {data && !data.officers.length && (
              <tr><td colSpan={10} className="px-3 py-4 font-mono text-xs text-[#6B7280]">No roster entries match “{q}”.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="font-mono text-[10px] text-[#9CA3AF] mt-3 border border-[#2C2D35] p-2">
        Historical reference roster (Baltimore Police Department active employees as of May 3, 2018). Personnel may have separated, transferred or been promoted since. Verify current status with BPD before operational use.
      </div>
    </div>
  );
}

export default function NCIC() {
  const [mode, setMode] = useState("query"); // query | entry
  const [tab, setTab] = useState("person");
  const [field, setField] = useState("name");
  const [value, setValue] = useState("");
  const [purpose, setPurpose] = useState("C");
  const [reason, setReason] = useState("");
  const [linkCc, setLinkCc] = useState("");
  const [codes, setCodes] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // entry form
  const emptyForm = { last_name: "", first_name: "", dob: "", sex: "", race: "", status: "CLEAR", caution: "", detail: "", state: "PA" };
  const [form, setForm] = useState(emptyForm);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [records, setRecords] = useState([]);
  const [profileRec, setProfileRec] = useState(null);

  // FBI Most Wanted (live federal fugitive data)
  const [fbiList, setFbiList] = useState([]);
  const [fbiQ, setFbiQ] = useState("");
  const [fbiBusy, setFbiBusy] = useState(false);
  const [fbiLoaded, setFbiLoaded] = useState(false);

  // "Attach FBI hit to a call" one-tap flow
  const [activeIncidents, setActiveIncidents] = useState([]);
  const [attachTarget, setAttachTarget] = useState(null);
  const [attachKind, setAttachKind] = useState("fugitive");
  const [attachCc, setAttachCc] = useState("");
  const [attaching, setAttaching] = useState(false);

  const loadActiveIncidents = async () => {
    try {
      const { data } = await client.get("/incidents", { params: { active: true } });
      setActiveIncidents(data || []);
    } catch { /* optional */ }
  };

  const openAttach = (hit, kind) => { setAttachTarget(hit); setAttachKind(kind === "missing" ? "missing" : "fugitive"); setAttachCc(linkCc.trim() || ""); loadActiveIncidents(); };

  const doAttach = async (cc) => {
    const target = (cc || attachCc).trim();
    if (!target) { toast.error("Pick or enter an incident Case#"); return; }
    setAttaching(true);
    try {
      await client.post("/incidents/attach-fbi-hit", {
        incident_cc: target, title: attachTarget.title, uid: attachTarget.uid, url: attachTarget.url, kind: attachKind });
      toast.error(`🚨 ${attachTarget.title} attached to CC ${target} — ${attachKind === "missing" ? "missing/exploited person" : "federal fugitive"} alert raised`);
      setAttachTarget(null); setAttachCc("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not attach to incident"); }
    finally { setAttaching(false); }
  };

  const loadFbi = async (q = "") => {
    setFbiBusy(true);
    try {
      const { data } = await client.get("/ncic/fbi-wanted", { params: q ? { q } : {} });
      setFbiList(data.items || []); setFbiLoaded(true);
    } catch (e) { toast.error(e.response?.data?.detail || "FBI Wanted service unavailable"); }
    finally { setFbiBusy(false); }
  };

  // Missing & Exploited (FBI Kidnappings & Missing Persons program)
  const [missingList, setMissingList] = useState([]);
  const [missingQ, setMissingQ] = useState("");
  const [missingBusy, setMissingBusy] = useState(false);
  const [missingLoaded, setMissingLoaded] = useState(false);
  const loadMissing = async (q = "") => {
    setMissingBusy(true);
    try {
      const { data } = await client.get("/ncic/missing-persons", { params: q ? { q } : {} });
      setMissingList(data.items || []); setMissingLoaded(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Missing Persons service unavailable"); }
    finally { setMissingBusy(false); }
  };

  useEffect(() => { if (tab === "federal" && !fbiLoaded) loadFbi(); }, [tab]); // eslint-disable-line
  useEffect(() => { if (tab === "missing" && !missingLoaded) loadMissing(); }, [tab]); // eslint-disable-line

  useEffect(() => { client.get("/ncic/purpose-codes").then((r) => setCodes(r.data)); }, []);
  useEffect(() => { setField(tab === "person" ? "name" : "plate"); }, [tab]);
  useEffect(() => { if (mode === "entry") loadRecords(); }, [mode]);

  const loadRecords = () => client.get("/ncic/records").then((r) => setRecords(r.data)).catch(() => {});

  const run = async () => {
    if (!value.trim()) { toast.error("Enter a query value"); return; }
    if (!reason.trim()) { toast.error("Reason-for-query is mandatory (CJIS)"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await client.post("/ncic/query", { query_type: tab, field, value, purpose_code: purpose, reason, incident_cc: linkCc.trim() || null });
      setResult(data);
      if ((data.federal_hits || []).length > 0) {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          [0, 0.3, 0.6].forEach((t) => { const o = ctx.createOscillator(); const g = ctx.createGain();
            o.type = "square"; o.frequency.value = 880; o.connect(g); g.connect(ctx.destination);
            g.gain.setValueAtTime(0.001, ctx.currentTime + t); g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + t + 0.02);
            g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.2); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.2); });
        } catch { /* audio optional */ }
        toast.error(`🚨 FEDERAL FUGITIVE HIT — ${data.federal_hits.length} FBI Most Wanted match(es)${data.federal_alert_incident ? ` · alerted CC ${data.federal_alert_incident}` : ""}`);
      } else if (data.count === 0) toast("No records returned");
    } catch (e) { toast.error(e.response?.data?.detail || "Query failed"); }
    finally { setBusy(false); }
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) { toast.error("Image files only (jpg, png, webp)"); return; }
    if (f.size > 8_000_000) { toast.error("Image too large (max 8MB)"); return; }
    setPhoto(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const saveRecord = async () => {
    if (!form.last_name.trim()) { toast.error("Last name is required"); return; }
    setSaving(true);
    try {
      const { data } = await client.post("/ncic/records", form);
      if (photo) {
        try {
          const fd = new FormData();
          fd.append("file", photo);
          await client.post(`/ncic/records/${data.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (pe) {
          // Record was created; only the photo failed. Do not encourage a duplicate re-save.
          toast.warning(`Record saved (${data.last_name}). Photo upload failed — ${pe.response?.data?.detail || "retry photo from the record"}.`);
          setForm(emptyForm); setPhoto(null); setPhotoPreview(null);
          loadRecords();
          return;
        }
      }
      toast.success(`Record entered — ${data.last_name}${data.first_name ? ", " + data.first_name : ""}`);
      setForm(emptyForm); setPhoto(null); setPhotoPreview(null);
      loadRecords();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const fields = tab === "person" ? PERSON_FIELDS : VEHICLE_FIELDS;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Layout>
      <PageHeader title="NCIC / CJIS Gateway" sub="Controlled gateway · purpose-coded · every transaction audited · agency records — state/federal CJIS link pending"
        right={<span className="font-mono text-[10px] text-[#F59E0B] flex items-center gap-1.5"><Lock size={12} /> CJI — FOR OFFICIAL USE ONLY</span>} />
      <div className="flex-1 overflow-y-auto p-5">
        <div className="max-w-4xl">
          {/* mode switch */}
          <div className="flex gap-1 mb-4 border-b border-[#2C2D35]">
            <button data-testid="ncic-mode-query" onClick={() => setMode("query")}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-head border-b-2 ${mode === "query" ? "border-[#3B82F6] text-white" : "border-transparent text-[#9CA3AF]"}`}><Search size={14} /> Query</button>
            <button data-testid="ncic-mode-entry" onClick={() => setMode("entry")}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-head border-b-2 ${mode === "entry" ? "border-[#3B82F6] text-white" : "border-transparent text-[#9CA3AF]"}`}><UserPlus size={14} /> New Record</button>
          </div>

          {mode === "query" && (
            <>
              {/* tabs */}
              <div className="flex gap-1 mb-3">
                <button data-testid="ncic-tab-person" onClick={() => setTab("person")}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-head border-b-2 ${tab === "person" ? "border-[#F59E0B] text-[#F59E0B]" : "border-transparent text-[#9CA3AF]"}`}><User size={14} /> Person</button>
                <button data-testid="ncic-tab-vehicle" onClick={() => setTab("vehicle")}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-head border-b-2 ${tab === "vehicle" ? "border-[#F59E0B] text-[#F59E0B]" : "border-transparent text-[#9CA3AF]"}`}><Car size={14} /> Vehicle</button>
                <button data-testid="ncic-tab-federal" onClick={() => setTab("federal")}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-head border-b-2 ${tab === "federal" ? "border-[#DC2626] text-[#DC2626]" : "border-transparent text-[#9CA3AF]"}`}><ShieldAlert size={14} /> FBI Most Wanted</button>
                <button data-testid="ncic-tab-missing" onClick={() => setTab("missing")}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-head border-b-2 ${tab === "missing" ? "border-[#F59E0B] text-[#F59E0B]" : "border-transparent text-[#9CA3AF]"}`}><User size={14} /> Missing &amp; Exploited</button>
                <button data-testid="ncic-tab-federaldb" onClick={() => setTab("federaldb")}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-head border-b-2 ${tab === "federaldb" ? "border-[#7C3AED] text-[#7C3AED]" : "border-transparent text-[#9CA3AF]"}`}><ShieldAlert size={14} /> Federal DB</button>
                <button data-testid="ncic-tab-offender" onClick={() => setTab("offender")}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-head border-b-2 ${tab === "offender" ? "border-[#F59E0B] text-[#F59E0B]" : "border-transparent text-[#9CA3AF]"}`}><ShieldAlert size={14} /> Sex Offender Registry</button>
                <button data-testid="ncic-tab-bpd" onClick={() => setTab("bpd")}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-head border-b-2 ${tab === "bpd" ? "border-[#3B82F6] text-[#3B82F6]" : "border-transparent text-[#9CA3AF]"}`}><User size={14} /> BPD Officer List</button>
              </div>

              {tab === "bpd" ? (
                <BpdRoster />
              ) : tab === "federaldb" ? (
                <FederalDb onAttach={openAttach} />
              ) : tab === "offender" ? (
                <><OffenderRegistry /><StateRegistries /></>
              ) : tab === "missing" ? (
                <div data-testid="ncic-missing">
                  <div className="flex gap-2 mb-3">
                    <input data-testid="ncic-missing-search" value={missingQ} onChange={(e) => setMissingQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadMissing(missingQ)}
                      placeholder="Search missing/exploited persons by name or alias…"
                      className="flex-1 bg-[#141518] border border-[#2C2D35] px-3 py-2.5 text-sm outline-none focus:border-[#F59E0B] font-mono" />
                    <button data-testid="ncic-missing-search-btn" onClick={() => loadMissing(missingQ)} disabled={missingBusy}
                      className="px-4 bg-[#F59E0B] text-black flex items-center gap-1.5 font-head text-sm">
                      {missingBusy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                    </button>
                  </div>
                  <div className="bg-[#F59E0B] text-black font-mono text-[11px] font-bold px-3 py-1.5 mb-3">
                    MISSING &amp; EXPLOITED — SOURCE: FBI KIDNAPPINGS &amp; MISSING PERSONS PROGRAM (api.fbi.gov) · LIVE
                  </div>
                  {missingBusy && missingList.length === 0 && <div className="text-sm font-mono text-[#9CA3AF] p-4 flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Loading missing &amp; exploited persons…</div>}
                  {!missingBusy && missingList.length === 0 && missingLoaded && <div className="text-sm font-mono text-[#9CA3AF] border border-[#2C2D35] p-3">No matching missing/exploited persons.</div>}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ncic-missing-list">
                    {missingList.map((f) => <FbiCard key={f.uid} f={f} onAttach={openAttach} kind="missing" />)}
                  </div>
                  <div className="font-mono text-[10px] text-[#9CA3AF] mt-3 border border-[#2C2D35] p-2">
                    Live from the FBI's public Kidnappings &amp; Missing Persons program (nationally reported / NCMEC-affiliated). Report sightings to the FBI at 1-800-CALL-FBI or NCMEC at 1-800-843-5678. Note: NCMEC's full registry requires a credentialed data agreement and is not an open public feed.
                  </div>
                </div>
              ) : tab === "federal" ? (
                <div data-testid="ncic-federal">
                  <div className="flex gap-2 mb-3">
                    <input data-testid="ncic-fbi-search" value={fbiQ} onChange={(e) => setFbiQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadFbi(fbiQ)}
                      placeholder="Search FBI fugitives by name or alias…"
                      className="flex-1 bg-[#141518] border border-[#2C2D35] px-3 py-2.5 text-sm outline-none focus:border-[#DC2626] font-mono" />
                    <button data-testid="ncic-fbi-search-btn" onClick={() => loadFbi(fbiQ)} disabled={fbiBusy}
                      className="px-4 bg-[#DC2626] text-white flex items-center gap-1.5 font-head text-sm">
                      {fbiBusy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                    </button>
                  </div>
                  <div className="bg-[#DC2626] text-white font-mono text-[11px] font-bold px-3 py-1.5 mb-3">
                    FEDERAL FUGITIVE DATA · SOURCE: FBI.GOV PUBLIC WANTED PROGRAM (api.fbi.gov) · LIVE
                  </div>
                  {fbiBusy && fbiList.length === 0 && <div className="text-sm font-mono text-[#9CA3AF] p-4 flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Loading federal fugitive data…</div>}
                  {!fbiBusy && fbiList.length === 0 && fbiLoaded && <div className="text-sm font-mono text-[#9CA3AF] border border-[#2C2D35] p-3">No matching FBI wanted subjects.</div>}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ncic-fbi-list">
                    {fbiList.map((f) => <FbiCard key={f.uid} f={f} onAttach={openAttach} />)}
                  </div>
                  <div className="font-mono text-[10px] text-[#9CA3AF] mt-3 border border-[#2C2D35] p-2">
                    Data sourced live from the FBI's public Wanted program. Verify identity and confirm with the originating FBI Field Office before any enforcement action.
                  </div>
                </div>
              ) : (
              <>
              {/* query row */}
              <div className="flex gap-2 mb-3">
                <select data-testid="ncic-field" value={field} onChange={(e) => setField(e.target.value)}
                  className="bg-[#141518] border border-[#2C2D35] px-3 py-2.5 text-sm font-mono outline-none">
                  {fields.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input data-testid="ncic-value" value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
                  placeholder={tab === "person" ? "Last name…" : "Plate or VIN…"}
                  className="flex-1 bg-[#141518] border border-[#2C2D35] px-3 py-2.5 text-sm outline-none focus:border-[#F59E0B] font-mono" />
                <button data-testid="ncic-search-btn" onClick={run} disabled={busy}
                  className="px-4 bg-[#F59E0B] text-black flex items-center gap-1.5 font-head text-sm">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                </button>
              </div>

              {/* mandatory purpose + reason */}
              <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-2 mb-4 border border-[#2C2D35] bg-[#0d0e11] p-3">
                <div>
                  <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Purpose Code *</label>
                  <select data-testid="ncic-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)}
                    className="mt-1 w-full bg-[#141518] border border-[#2C2D35] px-2 py-2 text-sm font-mono outline-none">
                    {Object.entries(codes).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Reason for Query * (audited)</label>
                  <input data-testid="ncic-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. traffic stop on subject vehicle" className="mt-1 w-full bg-[#141518] border border-[#2C2D35] px-2 py-2 text-sm outline-none focus:border-[#F59E0B] font-mono" />
                </div>
                <div className="md:col-span-2">
                  <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Link to Incident Case# (optional — flashes a federal alert on the incident for any FBI hit)</label>
                  <input data-testid="ncic-link-cc" value={linkCc} onChange={(e) => setLinkCc(e.target.value)}
                    placeholder="2026-#####" className="mt-1 w-full bg-[#141518] border border-[#2C2D35] px-2 py-2 text-sm outline-none focus:border-[#DC2626] font-mono" />
                </div>
              </div>

              {/* results */}
              {result && (
                <div data-testid="ncic-results">
                  <div className="bg-[#F59E0B] text-black font-mono text-[11px] font-bold px-3 py-1.5 mb-2">{result.banner}</div>
                  {result.results.length === 0 && <div className="text-sm font-mono text-[#9CA3AF] border border-[#2C2D35] p-3">NO RECORD — no matching CJI returned.</div>}
                  {result.results.map((r, i) => {
                    const c = STATUS_COLOR[r.status] || "#9CA3AF";
                    return (
                      <div key={i} data-testid={`ncic-result-${i}`} className="border-l-2 bg-[#141518] p-3 mb-2 flex gap-3" style={{ borderColor: c }}>
                        {tab === "person" && <AuthImg path={r.photo_path} className="shrink-0" size={64} />}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm font-bold" style={{ color: c }}>{r.status}</span>
                            {r.caution && <span className="font-mono text-[10px] font-bold text-white bg-[#DC2626] px-1.5 py-0.5 animate-pulse">{r.caution}</span>}
                          </div>
                          {tab === "person" ? (
                            <div className="text-sm font-head mt-1">{r.last_name}, {r.first_name} · DOB {r.dob}</div>
                          ) : (
                            <div className="text-sm font-head mt-1">{r.make} {r.model} {r.year} · Plate {r.plate} ({r.state}) · VIN {r.vin}</div>
                          )}
                          <div className="text-xs font-mono text-[#9CA3AF] mt-1">{r.detail}</div>
                        </div>
                      </div>
                    );
                  })}
                  {result.federal_hits && result.federal_hits.length > 0 && (
                    <div className="mt-3 border-2 border-[#DC2626] p-2 animate-pulse" data-testid="ncic-federal-hits" style={{ background: "rgba(220,38,38,0.08)" }}>
                      <div className="bg-[#DC2626] text-white font-mono text-[11px] font-bold px-3 py-1.5 mb-2 flex items-center gap-1.5">
                        <ShieldAlert size={13} /> 🚨 FEDERAL FUGITIVE HIT(S) · FBI.GOV WANTED PROGRAM (LIVE){result.federal_alert_incident ? ` · ALERTED CC ${result.federal_alert_incident}` : ""}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {result.federal_hits.map((f) => <FbiCard key={f.uid} f={f} onAttach={openAttach} />)}
                      </div>
                    </div>
                  )}
                  {result.local_warrants && result.local_warrants.length > 0 && (
                    <div className="mt-3 border-2 border-[#DC2626] p-2" data-testid="ncic-local-warrants" style={{ background: "rgba(220,38,38,0.08)" }}>
                      <div className="bg-[#DC2626] text-white font-mono text-[11px] font-bold px-3 py-1.5 mb-2 flex items-center gap-1.5">
                        <ShieldAlert size={13} /> 🚨 ACTIVE AGENCY WARRANT(S) ON FILE — RMS
                      </div>
                      <div className="space-y-2">
                        {result.local_warrants.map((w, i) => (
                          <div key={i} data-testid={`ncic-warrant-${i}`} className="border-l-2 border-[#DC2626] bg-[#141518] p-2.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-[10px] font-bold text-white bg-[#DC2626] px-1.5 py-0.5 uppercase">{(w.warrant_type || "").toUpperCase()} WARRANT</span>
                              {w.is_felony && <span className="font-mono text-[10px] font-bold text-white bg-[#7C2D12] px-1.5 py-0.5">FELONY</span>}
                              {w.extraditable && <span className="font-mono text-[10px] font-bold text-black bg-[#F59E0B] px-1.5 py-0.5">EXTRADITABLE</span>}
                              <span className="font-mono text-[10px] text-[#9CA3AF]">{w.warrant_number}</span>
                            </div>
                            <div className="text-sm font-head mt-1">{w.subject_name}{w.subject_dob ? ` · DOB ${w.subject_dob}` : ""}</div>
                            {w.charges && <div className="text-xs font-mono text-[#F87171] mt-0.5">Charges: {w.charges}</div>}
                            {(w.issuing_court || w.bond_amount) && <div className="text-xs font-mono text-[#9CA3AF] mt-0.5">{w.issuing_court || ""}{w.bond_amount ? ` · Bond ${w.bond_amount}` : ""}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="font-mono text-[10px] text-[#9CA3AF] mt-2 border border-[#2C2D35] p-2">
                    Transaction logged · {result.transaction.user} · {result.transaction.ori} · Purpose {result.transaction.purpose_code} · {new Date(result.transaction.ts).toLocaleString()} · Hit-confirmation required before enforcement action.
                  </div>
                </div>
              )}
              </>
              )}
            </>
          )}

          {mode === "entry" && (
            <>
              <div className="border border-[#2C2D35] bg-[#0d0e11] p-4 mb-5">
                <div className="font-head text-sm text-white mb-3 flex items-center gap-2"><UserPlus size={15} className="text-[#3B82F6]" /> Enter New Person Record</div>
                <div className="flex gap-4">
                  {/* photo */}
                  <div className="shrink-0">
                    <div className="w-28 h-28 border border-[#2C2D35] bg-[#141518] flex items-center justify-center overflow-hidden">
                      {photoPreview ? <img src={photoPreview} alt="preview" data-testid="ncic-photo-preview" className="w-full h-full object-cover" />
                        : <ImageOff size={22} className="text-[#4B5563]" />}
                    </div>
                    <label data-testid="ncic-photo-picker" className="mt-2 w-28 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-head bg-[#1E1F24] hover:bg-[#2C2D35] border border-[#2C2D35] cursor-pointer">
                      <Upload size={12} /> Photo
                      <input type="file" accept="image/png,image/jpeg,image/webp,image/heic,image/heif" className="hidden" onChange={onPhoto} />
                    </label>
                  </div>
                  {/* fields */}
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <Field label="Last Name *"><input data-testid="ncic-form-lastname" value={form.last_name} onChange={set("last_name")} className={inp} /></Field>
                    <Field label="First Name"><input data-testid="ncic-form-firstname" value={form.first_name} onChange={set("first_name")} className={inp} /></Field>
                    <Field label="Date of Birth"><input data-testid="ncic-form-dob" value={form.dob} onChange={set("dob")} placeholder="YYYY-MM-DD" className={inp} /></Field>
                    <Field label="State"><input data-testid="ncic-form-state" value={form.state} onChange={set("state")} className={inp} /></Field>
                    <Field label="Sex"><input data-testid="ncic-form-sex" value={form.sex} onChange={set("sex")} className={inp} /></Field>
                    <Field label="Race"><input data-testid="ncic-form-race" value={form.race} onChange={set("race")} className={inp} /></Field>
                    <Field label="Status">
                      <select data-testid="ncic-form-status" value={form.status} onChange={set("status")} className={inp}>
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="Caution"><input data-testid="ncic-form-caution" value={form.caution} onChange={set("caution")} placeholder="e.g. ARMED & DANGEROUS" className={inp} /></Field>
                    <div className="col-span-2">
                      <Field label="Detail / Warrant Info"><textarea data-testid="ncic-form-detail" value={form.detail} onChange={set("detail")} rows={2} className={inp} /></Field>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end mt-3">
                  <button data-testid="ncic-save-btn" onClick={saveRecord} disabled={saving}
                    className="px-5 py-2 bg-[#3B82F6] text-white font-head text-sm flex items-center gap-1.5 disabled:opacity-60">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Enter Record
                  </button>
                </div>
              </div>

              {/* entered records */}
              <div className="font-mono text-[10px] text-[#9CA3AF] uppercase mb-2">Entered Records ({records.length})</div>
              <div data-testid="ncic-records-list">
                {records.length === 0 && <div className="text-sm font-mono text-[#9CA3AF] border border-[#2C2D35] p-3">No records entered yet.</div>}
                {records.map((r) => {
                  const c = STATUS_COLOR[r.status] || "#9CA3AF";
                  return (
                    <div key={r.id} data-testid={`ncic-record-${r.id}`} className="border-l-2 bg-[#141518] p-3 mb-2 flex gap-3" style={{ borderColor: c }}>
                      <AuthImg path={r.photo_path} className="shrink-0" size={56} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-bold" style={{ color: c }}>{r.status}</span>
                          {r.caution && <span className="font-mono text-[10px] font-bold text-white bg-[#DC2626] px-1.5 py-0.5">{r.caution}</span>}
                        </div>
                        <div className="text-sm font-head mt-1">{r.last_name}, {r.first_name} · DOB {r.dob || "—"} · {r.state}</div>
                        {r.detail && <div className="text-xs font-mono text-[#9CA3AF] mt-1">{r.detail}</div>}
                        <LocalChargesBadges rec={r} onOpen={() => setProfileRec(r)} />
                        <div className="text-[10px] font-mono text-[#4B5563] mt-1">Entered by {r.entered_by} · {new Date(r.ts).toLocaleString()}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
      <PersonProfileCard name={profileRec ? `${profileRec.first_name || ""} ${profileRec.last_name || ""}`.trim() : ""} dob={profileRec?.dob} open={!!profileRec} onClose={() => setProfileRec(null)} />
      {attachTarget && (
        <div data-testid="ncic-attach-modal" className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setAttachTarget(null)}>
          <div className="bg-[#141518] border-2 border-[#DC2626] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between bg-[#DC2626] px-3 py-2">
              <div className="font-head font-bold text-white text-sm flex items-center gap-1.5"><ShieldAlert size={15} /> Attach FBI Hit to Call</div>
              <button data-testid="ncic-attach-close" onClick={() => setAttachTarget(null)} className="text-white"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="font-mono text-xs text-white">Subject: <b className="text-[#DC2626]">{attachTarget.title}</b></div>
              <div>
                <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Incident Case#</label>
                <input data-testid="ncic-attach-cc" value={attachCc} onChange={(e) => setAttachCc(e.target.value)}
                  placeholder="2026-#####" className="mt-1 w-full bg-[#0d0e11] border border-[#2C2D35] px-2 py-2 text-sm outline-none focus:border-[#DC2626] font-mono" />
              </div>
              {activeIncidents.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] text-[#9CA3AF] uppercase mb-1">Or tap an active call</div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {activeIncidents.slice(0, 30).map((i) => (
                      <button key={i.cc_number} data-testid={`ncic-attach-pick-${i.cc_number}`} onClick={() => doAttach(i.cc_number)} disabled={attaching}
                        className="w-full text-left px-2 py-1.5 bg-[#0d0e11] border border-[#2C2D35] hover:border-[#DC2626] transition-colors font-mono text-[11px] flex items-center justify-between">
                        <span className="text-white">{i.cc_number}</span>
                        <span className="text-[#9CA3AF] truncate ml-2">{i.call_type_label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button data-testid="ncic-attach-confirm" onClick={() => doAttach()} disabled={attaching}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 font-head font-bold text-white bg-[#DC2626] hover:bg-[#b91c1c] transition-colors disabled:opacity-50">
                {attaching ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />} Raise Federal Alert on Call
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const inp = "mt-1 w-full bg-[#141518] border border-[#2C2D35] px-2 py-2 text-sm outline-none focus:border-[#3B82F6] font-mono";
function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">{label}</label>
      {children}
    </div>
  );
}

function FbiCard({ f, onAttach, kind = "fbi" }) {
  const Placeholder = () => (
    f.url
      ? <a href={f.url} target="_blank" rel="noreferrer" data-testid={`ncic-fbi-photo-link-${f.uid}`}
          className="w-full h-full flex flex-col items-center justify-center gap-1 text-[#DC2626] hover:bg-[#DC2626]/10 transition-colors">
          <ShieldAlert size={20} /><span className="font-mono text-[8px] text-center leading-tight px-1">PHOTO ON<br/>FBI.GOV</span>
        </a>
      : <ImageOff size={20} className="text-[#4B5563]" />
  );
  return (
    <div data-testid={`ncic-${kind}-${f.uid}`} className="border-l-2 border-[#DC2626] bg-[#141518] p-3 flex gap-3">
      <div className="shrink-0 w-20 h-24 bg-[#0d0e11] border border-[#2C2D35] overflow-hidden flex items-center justify-center">
        <Placeholder />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-head text-sm font-bold text-white truncate">{f.title}</span>
          {f.status && f.status !== "NA" && <span className="font-mono text-[9px] font-bold text-white bg-[#DC2626] px-1.5 py-0.5">{f.status}</span>}
        </div>
        {f.poster_class && <div className="font-mono text-[10px] text-[#DC2626] uppercase mt-0.5">{f.poster_class}</div>}
        {f.reward_text && <div className="font-mono text-[10px] text-[#F59E0B] mt-1 line-clamp-2">{f.reward_text}</div>}
        {(f.aliases && f.aliases.length > 0) && <div className="font-mono text-[10px] text-[#9CA3AF] mt-1">AKA: {f.aliases.slice(0, 3).join(", ")}</div>}
        {f.description && <div className="text-xs font-mono text-[#9CA3AF] mt-1 line-clamp-3">{f.description}</div>}
        {f.caution && <div className="text-[10px] font-mono text-[#DC2626] mt-1 line-clamp-2">⚠ {f.caution}</div>}
        <div className="flex items-center gap-3 mt-1.5">
          {f.url && <a href={f.url} target="_blank" rel="noreferrer" data-testid={`ncic-fbi-link-${f.uid}`}
            className="inline-flex items-center gap-1 font-mono text-[10px] text-[#3B82F6] hover:underline">
            <ExternalLink size={10} /> FBI poster</a>}
          {onAttach && <button data-testid={`ncic-fbi-attach-${f.uid}`} onClick={() => onAttach(f, kind)}
            className="inline-flex items-center gap-1 font-mono text-[10px] font-bold text-white bg-[#DC2626] px-2 py-1 hover:bg-[#b91c1c] transition-colors">
            <Link2 size={10} /> Attach to Call</button>}
        </div>
      </div>
    </div>
  );
}
